/**
 * The Insights Lab's per-game table list (/api/v1/playground/tables),
 * shared by every panel that shows it and kept for the session.
 *
 * Stale-while-revalidate, not a TTL: the response also carries the game's
 * team names and facts, which people EDIT on other pages (Layers
 * Management, Data Management, Manage Games) with nothing in the SPA
 * observing it. So a panel draws the last copy at once, and every mount
 * still asks the server — the fresh answer replaces it when it lands.
 * Panels mounted together (the canvas and its chat) share one request.
 */
import { api } from './api'
import { pathSeg } from './urls'

const last = new Map<string, unknown>()
const inflight = new Map<string, Promise<unknown>>()

function url(gameCode: string, orgId: number | string): string {
  return `/api/v1/playground/tables?game_code=${pathSeg(gameCode)}&org_id=${orgId}`
}

/** The last table list this session saw for the game, if any. */
export function cachedTables<T>(gameCode: string, orgId: number | string): T | undefined {
  return last.get(url(gameCode, orgId)) as T | undefined
}

/** A fresh table list; concurrent callers share the request. */
export function fetchTables<T>(gameCode: string, orgId: number | string): Promise<T> {
  const key = url(gameCode, orgId)
  let p = inflight.get(key) as Promise<T> | undefined
  if (!p) {
    const req = api<T>(key)
    p = req
    inflight.set(key, req)
    // Settled in the same handlers, so a caller awaiting this request and
    // asking again at once starts a new one rather than reusing it.
    const done = () => { if (inflight.get(key) === req) inflight.delete(key) }
    req.then((d) => { last.set(key, d); done() }, done)
  }
  return p
}

/** Test seam: forget everything. */
export function resetTablesCache(): void {
  last.clear()
  inflight.clear()
}
